import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { motion } from "framer-motion";
import MailTextField from "../components/form/fields/MailTextField";
import Iconify from "../components/iconify/Iconify";
import { requestPasswordReset } from "../app/api/axios/services/public.service";

function IconCircle({ icon, color, size = 72, iconSize = 34 }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        bgcolor: alpha(color, 0.12),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mx: "auto",
        mb: 2,
      }}
    >
      <Iconify icon={icon} width={iconSize} sx={{ color }} />
    </Box>
  );
}

function CenteredLayout({ children }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(135deg,
          ${alpha(theme.palette.primary.lighter, 0.55)} 0%,
          ${alpha(theme.palette.secondary.lighter, 0.35)} 100%)`,
        p: { xs: 2, sm: 3 },
      }}
    >
      {children}
    </Box>
  );
}

export default function ForgotPasswordPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { handleSubmit, control } = useForm({
    defaultValues: { mail: "" },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setSubmitError("");
    try {
      await requestPasswordReset((data.mail || "").trim());
      setShowSuccess(true);
    } catch (error) {
      const status = error?.response?.status;
      const message = error?.response?.data?.message;
      if (status === 429) {
        setSubmitError(
          message ||
            "Zu viele Anfragen. Bitte warten Sie kurz und versuchen Sie es erneut.",
        );
      } else {
        setSubmitError(
          "Die Anfrage konnte gerade nicht verarbeitet werden. Bitte versuchen Sie es erneut.",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <CenteredLayout>
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.35 }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, sm: 5 },
              maxWidth: 480,
              width: "100%",
              borderRadius: 3,
              textAlign: "center",
              border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
            }}
          >
            <IconCircle
              icon="eva:email-fill"
              color={theme.palette.success.main}
              size={78}
              iconSize={40}
            />
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Anfrage erhalten
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
              Wenn ein Konto existiert, wurde eine E-Mail versendet.
            </Typography>
            <Button
              component={RouterLink}
              to="/login"
              variant="contained"
              color="secondary"
              sx={{ mt: 3 }}
            >
              Zur Anmeldung
            </Button>
          </Paper>
        </motion.div>
      </CenteredLayout>
    );
  }

  return (
    <CenteredLayout>
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        style={{ width: "100%" }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", sm: 480, md: 860 },
            mx: "auto",
          }}
        >
          <Grid
            container
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: theme.customShadows.z24,
            }}
          >
            {!isMobile && (
              <Grid
                item
                md={5}
                sx={{
                  background: `linear-gradient(155deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                  p: 5,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  color: "#fff",
                }}
              >
                <Iconify
                  icon="eva:unlock-fill"
                  width={52}
                  sx={{ mb: 3, opacity: 0.9 }}
                />
                <Typography variant="h4" fontWeight={800} sx={{ mb: 2, lineHeight: 1.25 }}>
                  Passwort vergessen?
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.84, lineHeight: 1.75 }}>
                  Geben Sie Ihre E-Mail-Adresse ein. Wenn ein Konto existiert, senden wir
                  einen sicheren Link zum Zurücksetzen des Passworts.
                </Typography>
              </Grid>
            )}

            <Grid
              item
              xs={12}
              md={7}
              component={Paper}
              elevation={0}
              square
              sx={{ p: { xs: 3, sm: 5 }, borderRadius: { xs: 3, md: 0 } }}
            >
              {isMobile ? (
                <Box sx={{ textAlign: "center", mb: 3 }}>
                  <IconCircle icon="eva:unlock-fill" color={theme.palette.primary.main} />
                </Box>
              ) : null}

              <Typography variant="h5" fontWeight={700} gutterBottom>
                Passwort zurücksetzen
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Bitte geben Sie die E-Mail-Adresse Ihres Kontos ein.
              </Typography>

              {submitError ? <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert> : null}

              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <MailTextField control={control} fullWidth />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={isSubmitting}
                  sx={{ mt: 2, py: 1.4 }}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <Iconify icon="eva:email-fill" width={18} />
                    )
                  }
                >
                  {isSubmitting ? "Wird gesendet..." : "Reset-Link anfordern"}
                </Button>
              </form>

              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{ mt: 3 }}
              >
                <Typography
                  component={RouterLink}
                  to="/login"
                  variant="body2"
                  sx={{
                    color: "primary.main",
                    fontWeight: 600,
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Zur Anmeldung
                </Typography>
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </motion.div>
    </CenteredLayout>
  );
}
